import {
  defaultSortParamName,
  defaultTakeParamName,
  getDefaultMultiListSortParamName,
  getDefaultMultiListTakeParamName,
} from '../sharedConstants';
import type { SearchPageSortOption } from '../sharedTypes';
import { getParsedQueryFromAsPath, getQueryValues, sanitizeTakeValue } from '../urlState';
import type { SearchListUrlState } from './types';

export const sanitizeSortValue = <SortValue extends string>(
  value: string | undefined,
  sortOptions: SearchPageSortOption<SortValue>[] | undefined,
  defaultSort: SortValue | null
) => {
  if (!value) {
    return defaultSort;
  }

  if (!sortOptions || sortOptions.some((option) => option.value === value)) {
    return value as SortValue;
  }

  return defaultSort;
};

type ResolveSearchListParamNamesArgs = {
  listKey: string;
  namespaced: boolean;
  takeParamName?: string;
  sortParamName?: string;
};

export const resolveSearchListParamNames = ({
  listKey,
  namespaced,
  takeParamName,
  sortParamName,
}: ResolveSearchListParamNamesArgs) => ({
  takeParamName:
    takeParamName ??
    (namespaced ? getDefaultMultiListTakeParamName(listKey) : defaultTakeParamName),
  sortParamName:
    sortParamName ??
    (namespaced ? getDefaultMultiListSortParamName(listKey) : defaultSortParamName),
});

type ParseSearchListUrlStateArgs<SortValue extends string> = {
  query: Record<string, string | string[] | undefined>;
  pageSize: number;
  takeParamName: string;
  sortParamName: string;
  sortOptions?: SearchPageSortOption<SortValue>[];
  defaultSort?: SortValue | null;
};

export const parseSearchListUrlState = <SortValue extends string>(
  args: ParseSearchListUrlStateArgs<SortValue>
): SearchListUrlState<SortValue> => {
  const { query, pageSize, takeParamName, sortParamName, sortOptions, defaultSort = null } = args;

  return {
    take: sanitizeTakeValue(getQueryValues(query[takeParamName])[0], pageSize),
    sort: sanitizeSortValue(getQueryValues(query[sortParamName])[0], sortOptions, defaultSort),
  };
};

type ReadSearchListUrlStateArgs<SortValue extends string> = Omit<
  ParseSearchListUrlStateArgs<SortValue>,
  'query'
> & {
  asPath: string;
};

export const readSearchListUrlStateFromAsPath = <SortValue extends string>(
  args: ReadSearchListUrlStateArgs<SortValue>
) => {
  const { asPath, ...parseArgs } = args;

  return parseSearchListUrlState({
    ...parseArgs,
    query: getParsedQueryFromAsPath(asPath),
  });
};

type AppendSearchListParamsArgs<SortValue extends string> = {
  params: URLSearchParams;
  urlState: SearchListUrlState<SortValue>;
  pageSize: number;
  takeParamName: string;
  sortParamName: string;
  defaultSort?: SortValue | null;
};

export const appendSearchListParams = <SortValue extends string>(
  args: AppendSearchListParamsArgs<SortValue>
) => {
  const { params, urlState, pageSize, takeParamName, sortParamName, defaultSort = null } = args;

  if (urlState.take !== pageSize) {
    params.set(takeParamName, String(urlState.take));
  }

  if (urlState.sort && urlState.sort !== defaultSort) {
    params.set(sortParamName, urlState.sort);
  }
};
